/**
 * Per-event GA payload builders — the single source of truth for all
 * load-bearing event strings in the Bridge Bar analytics layer.
 *
 * Why centralize here instead of per-component?
 *   With 4+ components each building their own payload strings, a single
 *   casing or breadcrumb drift produces silent dashboard corruption.
 *   Centralizing into constants and typed helpers means a typo fails at
 *   the unit-test boundary, not in production reporting (AD-7).
 *
 * Why does the `> popover` suffix appear on link clicks but NOT on toggles?
 *   The breadcrumb describes "where the user was when they acted".
 *   A toggle opens or closes the popover — it originates from outside it.
 *   A link click happens INSIDE the popover — `> popover` is the accurate
 *   location in the feature tree (AD-7, STEP-5 intentional distinction).
 *
 * Why is the tail-link identifier the stripped label, not a solutionId?
 *   Tail links (e.g. "Attach More Solutions") have no solutionId — their
 *   identity in GA reports is derived from the label with non-alphanumeric
 *   characters stripped, matching legacy behaviour (AC-3.3, OQ-2).
 *
 * Why use lowercased label for user-menu links?
 *   Legacy parity — the original spike lowercased link labels before
 *   building the eventResult string so historical rows are consistent
 *   regardless of label capitalisation changes (AC-4.2, AD-7).
 */

import {emit} from "./analyticsEmitter";
import {determineAction} from "./eventHelper";

// ── Shared constants ────────────────────────────────────────────────────────

/** Common event name required on every GA payload (AC-3.4, AC-5.2, AC-8.3). */
const EVENT = "userEvent";

/** Top-level feature identifier — present on every Bridge Bar event. */
const EVENT_FEATURE = "bridge bar";

/** Breadcrumb for solution-switcher events WITHOUT the popover suffix (toggle). */
const FEATURES_SOLUTION_SWITCHER = "bridge bar > solution switcher";

/** Breadcrumb for events that occur INSIDE the solution-switcher popover (link clicks). */
const FEATURES_SOLUTION_SWITCHER_POPOVER =
  "bridge bar > solution switcher > popover";

/** Breadcrumb for user-menu toggle events (non-`> popover`). */
const FEATURES_USER_MENU = "bridge bar > user menu";

/** Breadcrumb for events that occur INSIDE the user-menu popover (link clicks). */
const FEATURES_USER_MENU_POPOVER = "bridge bar > user menu > popover";

/** Breadcrumb for company-logo navigation events (AC-5.1, AD-7). */
const FEATURES_COMPANY_LOGO = "bridge bar > bridge bar company logo";

// ── Helper types ─────────────────────────────────────────────────────────────

/** Discriminated union describing the type of solution navigation event. */
export interface SolutionNavInput {
  /** `"group"` for a top-level solution group; `"tail"` for the tail link. */
  type: "group" | "tail";
  solutionId: string;
  label: string;
}

/** Input for a sub-link / solution application link (FR-8). */
export interface SolutionAppLinkInput {
  applicationId: string;
  label: string;
}

/** Input for a user-menu link. */
export interface UserMenuNavInput {
  label: string;
  /** True when the link has a JS callback instead of (or in addition to) a URL. */
  hasCallback: boolean;
}

// ── Internal identifier derivation ───────────────────────────────────────────

/**
 * Strips all non-alphanumeric characters from `label`.
 * Used for tail links that have no solutionId — the stripped label IS the
 * GA identifier so historical rows remain stable across label reformatting
 * (AC-3.3, OQ-2).
 *
 * Example: "Attach More Solutions" → "AttachMoreSolutions"
 */
function stripLabel(label: string): string {
  return label.replace(/[^a-zA-Z0-9]/g, "");
}

// ── Public emit helpers ──────────────────────────────────────────────────────

/**
 * Emit a user-menu popover toggle (open or close).
 *
 * Uses the NON-`> popover` breadcrumb — the toggle originates from outside
 * the popover (AC-4.1).
 *
 * @param state - `"expanded"` when opening, `"collapsed"` when closing
 * @param event - DOM MouseEvent for click-type classification (AC-6.1–6.3)
 * @param getTracker - optional custom tracker factory (AC-1.2)
 */
export function emitUserMenuToggle(
  state: "expanded" | "collapsed",
  event?: MouseEvent,
  getTracker?: () => unknown
): void {
  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_USER_MENU,
      eventElement: "user menu",
      eventResult: `popover ${state}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit an Application Switcher popover toggle (open or close).
 *
 * Uses the NON-`> popover` breadcrumb — the user is toggling the popover
 * from the outside, not acting within it (AC-3.1). The User Menu has its own
 * `emitUserMenuToggle`; this helper is Application-Switcher-specific.
 *
 * @param state - `"expanded"` when opening, `"collapsed"` when closing
 * @param event - DOM MouseEvent for click-type classification (AC-6.1–6.3)
 * @param getTracker - optional custom tracker factory (AC-1.2)
 */
export function emitApplicationSwitcherToggle(
  state: "expanded" | "collapsed",
  event?: MouseEvent,
  getTracker?: () => unknown
): void {
  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_SOLUTION_SWITCHER,
      eventElement: "solution switcher",
      eventResult: `popover ${state}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit a composed-solution accordion toggle (expand or collapse).
 *
 * Net-new event with no legacy contract — the composed-solution accordion was
 * originally left untracked (AC-8.5 covers the *sub-link* events, not the
 * accordion itself). Uses the `> popover` breadcrumb because the accordion is
 * an interaction INSIDE the open popover. The eventResult carries the
 * solutionId so reports show WHICH group was toggled (parallels the sub-link
 * carrying its applicationId in eventResult).
 *
 * @param solutionId - the composed solution's GA identifier
 * @param state - `"expanded"` when opening, `"collapsed"` when closing
 * @param event - DOM MouseEvent for click-type classification (AC-6.1–6.3)
 * @param getTracker - optional custom tracker factory (AC-1.2)
 */
export function emitSolutionGroupToggle(
  solutionId: string,
  state: "expanded" | "collapsed",
  event?: MouseEvent,
  getTracker?: () => unknown
): void {
  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_SOLUTION_SWITCHER_POPOVER,
      eventElement: "solution group toggle",
      eventResult: `${state} ${solutionId}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit a solution group link click or tail link click from the solution switcher.
 *
 * Identifier derivation:
 *   - `"group"` → `solutionId` (stable API identifier)
 *   - `"tail"` → stripped label (no solutionId available, AC-3.3, OQ-2)
 *
 * Uses the `> popover` breadcrumb because the click occurs inside the popover
 * (AC-3.2, AD-7).
 *
 * @param input - solution nav descriptor
 * @param event - DOM MouseEvent for click-type classification
 * @param getTracker - optional custom tracker factory
 */
export function emitSolutionNav(
  input: SolutionNavInput,
  event: MouseEvent | undefined,
  getTracker?: () => unknown
): void {
  const identifier =
    input.type === "group" ? input.solutionId : stripLabel(input.label);

  const eventElement =
    input.type === "group" ? "solution group link" : "solution user link";

  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_SOLUTION_SWITCHER_POPOVER,
      eventElement,
      eventResult: `navigated to ${identifier}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit a solution application sub-link click (FR-8, AC-8.1–8.3).
 *
 * The identifier is `applicationId` — each application within a composed
 * solution group has a stable API ID used as the GA result value (AD-7).
 * Reuses the `> popover` breadcrumb because sub-links live inside the popover.
 *
 * @param input - application link descriptor
 * @param event - DOM MouseEvent for click-type classification
 * @param getTracker - optional custom tracker factory
 */
export function emitSolutionAppLink(
  input: SolutionAppLinkInput,
  event: MouseEvent | undefined,
  getTracker?: () => unknown
): void {
  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_SOLUTION_SWITCHER_POPOVER,
      eventElement: "solution application link",
      eventResult: `navigated to ${input.applicationId}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit a user-menu link interaction (AC-4.2, AC-4.3, AC-4.4).
 *
 * Why lowercase?
 *   Label capitalisation can vary over time; lowercasing keeps GA keys stable
 *   across label text changes so historical aggregation isn't broken (AD-7).
 *
 * Why two prefixes (`"navigated to "` vs `"invoked callback for "`)?
 *   Callback-backed links trigger JS code (e.g. logout) instead of — or in
 *   addition to — navigating; the prefix lets dashboards split these two
 *   interaction types without a separate event type (AC-4.4).
 *
 * @param input - user-menu link descriptor
 * @param event - DOM MouseEvent for click-type classification
 * @param getTracker - optional custom tracker factory
 */
export function emitUserMenuNav(
  input: UserMenuNavInput,
  event: MouseEvent | undefined,
  getTracker?: () => unknown
): void {
  const lowercasedLabel = input.label.toLowerCase();
  const resultPrefix = input.hasCallback
    ? "invoked callback for "
    : "navigated to ";

  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_USER_MENU_POPOVER,
      eventElement: `${lowercasedLabel} link`,
      eventResult: `${resultPrefix}${lowercasedLabel}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}

/**
 * Emit a company-logo navigation event (AC-5.1).
 *
 * The `eventResult` carries the full landing page URL so dashboards can
 * verify users are reaching the correct root destination (AC-5.1).
 *
 * @param landingPageUrl - the URL the logo navigates to
 * @param event - DOM MouseEvent for click-type classification
 * @param getTracker - optional custom tracker factory
 */
export function emitLogoNav(
  landingPageUrl: string,
  event: MouseEvent | undefined,
  getTracker?: () => unknown
): void {
  emit(
    {
      event: EVENT,
      eventFeature: EVENT_FEATURE,
      eventFeatures: FEATURES_COMPANY_LOGO,
      eventElement: "bridge-bar-company-logo",
      eventResult: `navigated to ${landingPageUrl}`,
      eventAction: determineAction(event)
    },
    getTracker
  );
}
